import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Typography, Button, Grid2, InputAdornment } from '@mui/material';
import ModalBox from '../themes/overrides/ModalBox';
import { useDispatch, useSelector } from 'react-redux';
import { getAllEntries } from '../actions/entryActions';
import { CircularProgress } from '@mui/material';
import TextFieldInput from '../themes/overrides/TextFieldInput';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useParams } from 'react-router-dom';

const AddExistingEntryPopup = ({ open, onClose, onConfirm }) => {
    const dispatch = useDispatch();
    const entries = useSelector((state) => state.entry.entries) || [];
    const { workspaceId } = useParams();
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const observer = useRef();
    const ITEMS_PER_PAGE = 20;

    useEffect(() => {
        dispatch(getAllEntries(workspaceId));
    }, [dispatch, workspaceId]);

    const filteredEntries = entries.filter(entry =>
        entry?.identifier?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const lastElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                setPage(prevPage => prevPage + 1);
            }
        });
        
        if (node) observer.current.observe(node);
    }, [loading]);

    const paginatedEntries = filteredEntries.slice(0, page * ITEMS_PER_PAGE);
    const hasMore = paginatedEntries.length < filteredEntries.length;

    const handleConfirm = () => {
        onConfirm(selectedEntries);
        setSelectedEntries([]);
        onClose()
    }

    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    return (
        <div>
            <Modal open={open} onClose={onClose}>
                <ModalBox sx={{
                    width: { xs: '95%', sm: '600px', md: '700px' },
                    maxHeight: { xs: '95vh', sm: '90vh' }, padding: { xs: 2, md: 4 }
                }}>
                    <TextFieldInput
                        fullWidth
                        size="small"
                        placeholder="Search entries..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: {
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                }
                            }
                        }}
                    />
                    {entries && entries.length > 0 ? (
                        <>
                            {filteredEntries.length > 0 ? (
                                paginatedEntries.map((entry, index) => (
                                    <Grid2
                                        ref={index === paginatedEntries.length - 1 ? lastElementRef : null}
                                        key={entry._id}
                                        padding={2} marginTop={1} container
                                        onClick={() => {
                                            setSelectedEntries(prevSelectedEntries => {
                                                const isSelected = prevSelectedEntries.includes(entry._id);
                                                if (isSelected) {
                                                    return prevSelectedEntries.filter(id => id !== entry._id);
                                                }
                                                return [...prevSelectedEntries, entry._id];
                                            });
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            border: selectedEntries.includes(entry._id) ? '1.5px solid #7E5CAD' : '1.5px solid #ECECEC',
                                            borderRadius: 1,
                                            transition: 'border 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5'
                                            }
                                        }}
                                    >
                                        <Grid2 size={{ xs: 8, md: 10 }}>
                                            <Typography variant="h6" component="h2">
                                                {entry.identifier}
                                            </Typography>
                                        </Grid2>
                                        <Grid2 size={{ xs: 4, md: 2 }}
                                            textAlign='right'>
                                            {entry.published === true ? (
                                                <Typography
                                                    sx={{
                                                        color: 'green',
                                                        backgroundColor: '#e6ffe6',
                                                        display: 'inline-block',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: 13
                                                    }}
                                                >
                                                    Published
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    sx={{
                                                        color: '#856404',
                                                        backgroundColor: '#fff3cd',
                                                        display: 'inline-block',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: 13
                                                    }}
                                                >
                                                    Draft
                                                </Typography>
                                            )}
                                        </Grid2>
                                    </Grid2>

                                ))

                            ) : (
                                <Typography marginTop={1} align="center" color="textSecondary">
                                    No matching entries found
                                </Typography>
                            )}
                            {hasMore && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                            <Box sx={{
                                position: 'sticky',
                                bottom: { xs: -25, md: -34 },
                                pt: 2,
                                pb: 2,
                                backgroundColor: 'white',
                                borderTop: '1px solid #ECECEC',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 1
                            }}>
                                <Button onClick={onClose} variant="outlined" color="secondary">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleConfirm}
                                    variant="contained"
                                    color="primary"
                                    disabled={selectedEntries.length === 0}
                                >
                                    Done ({selectedEntries.length})
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <Box>
                            <Typography>No entries found</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                                <Button onClick={onClose} variant="outlined" color="secondary">
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    )}
                </ModalBox>
            </Modal>
        </div >
    );
};
AddExistingEntryPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
};
export default AddExistingEntryPopup;